import React, { useState, useEffect } from 'react';
import { Plus, Search, DollarSign, Package, Eye, Edit, Trash2, BarChart2, Wallet, Table, Grid3X3, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { api, Product, StockProduct } from '../services/api';
import { ProductViewModal } from './ProductViewModal';
import { ProductForm } from './ProductForm';
import toast from 'react-hot-toast';

export const Products: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [stockData, setStockData] = useState<StockProduct[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [deleteConfirmProduct, setDeleteConfirmProduct] = useState<Product | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState<Partial<Product>>({
    name: '', category: 'Geral', basePrice: 0, stockQuantity: 0, 
    unit: 'un', images: [], shortDescription: '', sku: ''
  });

  const generateSKU = (name: string) => `${name.substring(0,3).toUpperCase()}-${Date.now().toString().slice(-4)}`;

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [productsData, stockOverview] = await Promise.all([
        api.products.getAll(),
        api.products.getStockOverview()
      ]);
      setProducts(productsData);
      setStockData(stockOverview);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    }
  };

  const stats = {
    totalRegistado: products.length,
    totalPublicado: products.filter(p => p.isActive).length,
    totalNaoPublicado: products.filter(p => !p.isActive).length,
    valorTotalStock: stockData.reduce((acc, p) => acc + (p.basePrice * p.stockQuantity), 0),
    lucroPotencialStock: stockData.reduce((acc, p) => acc + p.potentialProfit, 0),
    lucroRealizado: stockData.reduce((acc, p) => acc + p.totalProfit, 0),
  };

  const handleOpenForm = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData(product);
    } else {
      setEditingProduct(null);
      setFormData({ name: '', category: 'Geral', basePrice: 0, stockQuantity: 0, unit: 'un', images: [], shortDescription: '', sku: '' });
    }
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...formData, sku: formData.sku || generateSKU(formData.name!) };
      if (editingProduct) {
        await api.products.update(editingProduct._id, payload);
        toast.success('Atualizado!');
      } else {
        await api.products.create(payload);
        toast.success('Criado!');
      }
      setShowForm(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    setDeleteConfirmProduct(product);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmProduct) return;
    try {
      setIsDeleting(true);
      await api.products.delete(deleteConfirmProduct._id);
      toast.success('Produto eliminado com sucesso');
      setDeleteConfirmProduct(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar produto');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredStock = stockData.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStockStatus = (p: StockProduct) => {
    if (p.stockQuantity <= 0) return { label: 'Sem stock', color: 'bg-red-600 text-white', icon: Minus };
    if (p.stockQuantity <= p.minStockLevel) return { label: `Stock baixo: ${p.stockQuantity}`, color: 'bg-amber-100 text-amber-700', icon: TrendingDown };
    return { label: `${p.stockQuantity} em stock`, color: 'bg-green-100 text-green-700', icon: TrendingUp };
  };

  const ProfitIcon = ({ value }: { value: number }) => {
    if (value > 0) return <TrendingUp size={14} className="text-green-500" />;
    if (value < 0) return <TrendingDown size={14} className="text-red-500" />;
    return <Minus size={14} className="text-gray-400" />;
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-50 rounded-xl">
            <Package className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-black text-gray-900 uppercase tracking-tighter">
              Gestão de Stock
            </h2>
            <p className="text-xs text-gray-500 font-medium hidden sm:block">
              Produtos, quantidades, lucro por unidade
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex bg-gray-100 rounded-xl p-1 border border-gray-200">
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2 rounded-lg transition ${viewMode === 'cards' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Grid3X3 size={18} />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg transition ${viewMode === 'table' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Table size={18} />
            </button>
          </div>
          <button 
            onClick={() => handleOpenForm()} 
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl flex items-center gap-2 font-bold shadow-lg shadow-blue-900/10 transition-all active:scale-95 text-sm"
          >
            <Plus size={18} />
            <span>Novo Produto</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-100">
          <p className="text-xs text-gray-500 font-medium mb-1">Total Registado</p>
          <p className="text-2xl font-black text-gray-800">{stats.totalRegistado}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-100">
          <p className="text-xs text-gray-500 font-medium mb-1">Publicados</p>
          <p className="text-2xl font-black text-green-600">{stats.totalPublicado}</p>
        </div>
        <div className="bg-gray-900 p-4 rounded-xl">
          <p className="text-xs text-gray-400 font-medium mb-1">Valor em Stock</p>
          <p className="text-2xl font-black text-white">{stats.valorTotalStock.toLocaleString('pt-MZ')} <span className="text-xs text-blue-400">MT</span></p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl">
          <p className="text-xs text-emerald-600 font-medium mb-1">Lucro Potencial</p>
          <p className="text-2xl font-black text-emerald-700">{stats.lucroPotencialStock.toLocaleString('pt-MZ')} <span className="text-xs">MT</span></p>
        </div>
        <div className="bg-green-600 p-4 rounded-xl">
          <p className="text-xs text-white/80 font-medium mb-1">Lucro Realizado</p>
          <p className="text-2xl font-black text-white">{stats.lucroRealizado.toLocaleString('pt-MZ')} <span className="text-xs opacity-75">MT</span></p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Buscar produto por nome ou SKU..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
        />
      </div>

      {/* Cards View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredStock.map(p => {
            const status = getStockStatus(p);
            return (
              <div key={p._id} onClick={() => {
                const prod = products.find(pr => pr._id === p._id);
                if (prod) setViewingProduct(prod);
              }} className="bg-white rounded-xl border border-gray-100 hover:shadow-lg transition-all cursor-pointer group">
                {/* Image */}
                <div className="h-28 bg-gray-50 rounded-t-xl overflow-hidden relative">
                  {p.images?.[0] ? (
                    <img src={p.images[0]} className="w-full h-full object-cover" alt={p.name} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-10 h-10 text-gray-300" />
                    </div>
                  )}
                  <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${status.color}`}>
                    <status.icon size={12} className="inline mr-1" />
                    {status.label}
                  </div>
                </div>
                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-[9px] font-bold text-blue-500 uppercase">{p.category}</span>
                      <h4 className="font-bold text-gray-900 text-sm leading-tight">{p.name}</h4>
                      {p.sku && <p className="text-[10px] text-gray-400 font-mono">{p.sku}</p>}
                    </div>
                    <p className="font-black text-gray-900">{p.basePrice.toLocaleString('pt-MZ')} <span className="text-[9px] font-bold text-gray-400">MT</span></p>
                  </div>

                  {/* Stock + Profit mini grid */}
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-50">
                    <div>
                      <p className="text-[9px] text-gray-400 uppercase font-bold">Stock</p>
                      <p className="text-sm font-bold text-gray-700">{p.stockQuantity} <span className="text-[9px] font-normal text-gray-400">{p.unit}</span></p>
                    </div>
                    <div>
                      <p className="text-[9px] text-gray-400 uppercase font-bold">Vendido</p>
                      <p className="text-sm font-bold text-gray-700">{p.totalSold} <span className="text-[9px] font-normal text-gray-400">{p.unit}</span></p>
                    </div>
                    <div>
                      <p className="text-[9px] text-gray-400 uppercase font-bold">Lucro/Un</p>
                      <p className={`text-sm font-bold flex items-center gap-1 ${p.profitPerUnit > 0 ? 'text-green-600' : p.profitPerUnit < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        <ProfitIcon value={p.profitPerUnit} />
                        {p.profitPerUnit.toLocaleString('pt-MZ')} MT
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] text-gray-400 uppercase font-bold">Lucro Total</p>
                      <p className={`text-sm font-bold ${p.totalProfit > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                        {p.totalProfit.toLocaleString('pt-MZ')} MT
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={(e) => {
                      e.stopPropagation();
                      const prod = products.find(pr => pr._id === p._id);
                      if (prod) handleOpenForm(prod);
                    }} className="flex-1 py-1.5 bg-gray-50 rounded-lg text-xs font-bold text-gray-600 hover:bg-amber-50 hover:text-amber-600 transition flex items-center justify-center gap-1">
                      <Edit size={12} /> Editar
                    </button>
                    <button onClick={(e) => {
                      e.stopPropagation();
                      const prod = products.find(pr => pr._id === p._id);
                      if (prod) handleDeleteProduct(prod);
                    }} className="flex-1 py-1.5 bg-gray-50 rounded-lg text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 transition flex items-center justify-center gap-1">
                      <Trash2 size={12} /> Eliminar
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {filteredStock.length === 0 && (
            <div className="col-span-full text-center py-16 text-gray-500">
              <Package className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <p className="font-bold">Nenhum produto encontrado</p>
              <p className="text-sm">{searchTerm ? 'Tente outro termo de busca' : 'Clique em "Novo Produto" para começar'}</p>
            </div>
          )}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider">Produto</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider">SKU</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider">Categoria</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Preço</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Custo</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Lucro/Un</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Stock</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Vendido</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Lucro Total</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-center">Estado</th>
                <th className="px-4 py-3 font-bold text-gray-600 text-[10px] uppercase tracking-wider text-right">Acções</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredStock.map(p => {
                const status = getStockStatus(p);
                return (
                  <tr key={p._id} className="hover:bg-blue-50/30 transition cursor-pointer" onClick={() => {
                    const prod = products.find(pr => pr._id === p._id);
                    if (prod) setViewingProduct(prod);
                  }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          {p.images?.[0] ? <img src={p.images[0]} className="w-full h-full object-cover" /> : <Package className="w-4 h-4 m-2 text-gray-400" />}
                        </div>
                        <span className="font-semibold text-gray-800">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">{p.sku}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{p.category}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{p.basePrice.toLocaleString('pt-MZ')}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{p.costPrice > 0 ? p.costPrice.toLocaleString('pt-MZ') : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold flex items-center justify-end gap-1 ${p.profitPerUnit > 0 ? 'text-green-600' : p.profitPerUnit < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        <ProfitIcon value={p.profitPerUnit} />
                        {p.profitPerUnit.toLocaleString('pt-MZ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{p.stockQuantity}</td>
                    <td className="px-4 py-3 text-right font-semibold text-blue-600">{p.totalSold}</td>
                    <td className="px-4 py-3 text-right font-semibold text-green-700">{p.totalProfit.toLocaleString('pt-MZ')}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[9px] font-bold px-2 py-1 rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={(e) => {
                          e.stopPropagation();
                          const prod = products.find(pr => pr._id === p._id);
                          if (prod) handleOpenForm(prod);
                        }} className="p-1.5 hover:bg-gray-100 rounded-lg transition">
                          <Edit size={14} className="text-gray-500" />
                        </button>
                        <button onClick={(e) => {
                          e.stopPropagation();
                          const prod = products.find(pr => pr._id === p._id);
                          if (prod) handleDeleteProduct(prod);
                        }} className="p-1.5 hover:bg-red-50 rounded-lg transition">
                          <Trash2 size={14} className="text-gray-500 hover:text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredStock.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-gray-500">
                    Nenhum produto encontrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Forms & Modals */}
      {showForm && (
        <ProductForm 
          formData={formData} 
          setFormData={setFormData} 
          onSubmit={handleSubmit} 
          onClose={() => setShowForm(false)} 
          isEditing={!!editingProduct} 
        />
      )}
      {viewingProduct && (
        <ProductViewModal 
          product={viewingProduct} 
          onClose={() => setViewingProduct(null)} 
        />
      )}
      {deleteConfirmProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-black text-gray-900 uppercase">Confirmar Eliminação</h2>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-600 font-medium">Tem a certeza que deseja eliminar o produto:</p>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="font-bold text-gray-900">{deleteConfirmProduct.name}</p>
                <p className="text-sm text-gray-500 font-mono">{deleteConfirmProduct.sku}</p>
              </div>
              <p className="text-sm text-red-600 font-medium">⚠️ Esta ação é irreversível.</p>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setDeleteConfirmProduct(null)} disabled={isDeleting} className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-bold disabled:opacity-50">Cancelar</button>
              <button onClick={confirmDelete} disabled={isDeleting} className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                {isDeleting ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Eliminando...</>
                ) : (
                  <><Trash2 size={16} /> Eliminar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};